/**
 * Shopify cart attribution hook.
 *
 * Intercepts the storefront's Ajax cart-add API (`/cart/add.js`,
 * `/cart/add`) and stamps every request with
 * `attributes[ps_session]: <sessionId>`. Shopify exposes cart
 * attributes as order note attributes, so when the
 * `orders/create` webhook fires we can match the purchase back to the
 * customer's try-on session on our backend.
 *
 * Also posts a lightweight `ADD_TO_CART_FROM_TRYON` event to our App
 * Proxy `/events/cart-add` endpoint so the admin funnel shows the step
 * regardless of whether the customer finishes checkout.
 *
 * No merchant integration required — dropping the storefront bundle
 * auto-installs this hook.
 */

import { getOrCreateSessionId } from "../session";
import { logSizeAccepted } from "./events";

const ATTR_KEY = "ps_session";
const PROXY_BASE = "/apps/primestyle";
const CART_ADD_ENDPOINTS = ["/cart/add.js", "/cart/add"];
const CART_ADD_DEDUPE_MS = 8_000;
const TAG = "[primestyle-cart]";

// We only care whether a try-on completed for this product in this
// session. The React widget sets this whenever `ps:complete` fires.
let lastCompletedProduct: { productId: string | null; productTitle: string | null } | null = null;
/** Last size the user picked for a product, captured from the widget state. */
let lastSizeSelection: {
	productId: string | null;
	productTitle: string | null;
	size: string | null;
	recommendedSize: string | null;
} | null = null;
const recentCartAddEvents = new Map<string, number>();

export function setLastCompletedProduct(
	productId: string | null,
	productTitle: string | null,
): void {
	lastCompletedProduct = { productId, productTitle };
}

/**
 * Called by the React widget whenever the user actively picks a size in
 * the size-result view. The cart-hook then fires a SIZE_RECOMMENDATION_ACCEPTED
 * event when this product gets added to cart — a clearer "user committed"
 * signal than "user clicked a pill once".
 *
 * Side-effect: proactively stamp the current cart with `attributes[ps_session]`
 * via `/cart/update.js`. This is the primary attribution path now — works
 * regardless of how the buyer eventually checks out (Ajax cart, Buy It Now,
 * Shop Pay), because the attribute is already on the cart object before
 * the buyer adds anything. The legacy fetch/form-submit interception below
 * stays as a defense-in-depth fallback for older themes.
 */
export function setLastSizeSelection(input: {
	productId: string | null;
	productTitle: string | null;
	size: string | null;
	recommendedSize: string | null;
}): void {
	lastSizeSelection = { ...input };
	// Fire-and-forget — never block the widget on a network call.
	void stampSessionOnCart();
}

function isShopifyStorefront(): boolean {
	if (typeof window === "undefined") return false;
	try {
		const w = window as unknown as { Shopify?: { shop?: string } };
		return Boolean(w.Shopify?.shop);
	} catch {
		return false;
	}
}

/**
 * POST `/cart/update.js` to set `attributes[ps_session]` on the buyer's
 * cart. Idempotent: setting the same value repeatedly is harmless.
 * Runs only on Shopify storefronts where `/cart/update.js` exists.
 */
async function stampSessionOnCart(): Promise<void> {
	if (typeof window === "undefined" || typeof fetch !== "function") return;
	if (!isShopifyStorefront()) return;
	const sessionId = getOrCreateSessionId();
	try {
		await fetch("/cart/update.js", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ attributes: { [ATTR_KEY]: sessionId } }),
			credentials: "same-origin",
			keepalive: true,
		});
	} catch (err) {
		// Best-effort — never throw out of the widget event path.
		console.warn(TAG, "cart-attributes update failed", err);
	}
}

function flushSizeAcceptedEvent(): void {
	if (!lastSizeSelection) return;
	logSizeAccepted({
		productId: lastSizeSelection.productId ?? undefined,
		productTitle: lastSizeSelection.productTitle ?? undefined,
		size: lastSizeSelection.size ?? undefined,
		recommendedSize: lastSizeSelection.recommendedSize ?? undefined,
	});
	lastSizeSelection = null;
}

function isCartAddUrl(input: string): boolean {
	return CART_ADD_ENDPOINTS.some((p) => input.includes(p));
}

function getShopDomain(): string | null {
	if (typeof window === "undefined") return null;
	try {
		// Shopify storefront exposes the shop domain on window.Shopify.shop
		const w = window as unknown as { Shopify?: { shop?: string } };
		if (w.Shopify?.shop) return w.Shopify.shop;
	} catch {
		/* noop */
	}
	return typeof window.location !== "undefined" ? window.location.hostname : null;
}

function cleanRecentCartAdds(now: number): void {
	for (const [key, seenAt] of recentCartAddEvents) {
		if (now - seenAt > CART_ADD_DEDUPE_MS) recentCartAddEvents.delete(key);
	}
}

function cartAddDedupeKey(
	sessionId: string,
	product: {
		productId?: string | null;
		variantId?: string | null;
		size?: string | null;
		recommendedSize?: string | null;
		quantity?: number;
	},
): string {
	const productKey = product.variantId || product.productId || "unknown-product";
	const sizeKey = product.size || product.recommendedSize || "unknown-size";
	const quantityKey = product.quantity ?? 1;
	return [sessionId, productKey, sizeKey, quantityKey].map((part) => String(part).trim().toLowerCase()).join("|");
}

async function postCartAddEvent(product: {
	productId?: string | null;
	productTitle?: string | null;
	variantId?: string | null;
	size?: string | null;
	recommendedSize?: string | null;
	quantity?: number;
}): Promise<void> {
	const shopDomain = getShopDomain();
	if (!shopDomain) return;
	const sessionId = getOrCreateSessionId();
	const dedupeKey = cartAddDedupeKey(sessionId, product);
	const now = Date.now();
	cleanRecentCartAdds(now);
	if (recentCartAddEvents.has(dedupeKey)) return;
	recentCartAddEvents.set(dedupeKey, now);
	try {
		await fetch(`${PROXY_BASE}/events/cart-add`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				shopDomain,
				sessionId,
					productId: product.productId ?? undefined,
					productTitle: product.productTitle ?? undefined,
					variantId: product.variantId ?? undefined,
					size: product.size ?? undefined,
					recommendedSize: product.recommendedSize ?? undefined,
					quantity: product.quantity ?? 1,
				}),
			keepalive: true,
		});
	} catch (err) {
		recentCartAddEvents.delete(dedupeKey);
		// Best-effort — never break the cart add.
		console.warn(TAG, "failed to post cart-add event", err);
	}
}

type CartProductContext = {
	productId?: string | null;
	productTitle?: string | null;
	variantId?: string | null;
	size?: string | null;
	recommendedSize?: string | null;
	quantity?: number;
};

function stringValue(raw: unknown): string | null {
	if (raw == null || raw === "") return null;
	return String(raw);
}

function numberValue(raw: unknown): number | undefined {
	const n = Number(raw);
	return Number.isFinite(n) && n > 0 ? n : undefined;
}

function tryParseJson(raw: unknown): unknown {
	if (typeof raw !== "string") return raw;
	try {
		return JSON.parse(raw);
	} catch {
		return raw;
	}
}

function extractContextFromObject(raw: unknown): CartProductContext {
	if (!raw || typeof raw !== "object") return {};
	const obj = raw as Record<string, unknown>;
	const firstItem = Array.isArray(obj.items) && obj.items[0] && typeof obj.items[0] === "object"
		? (obj.items[0] as Record<string, unknown>)
		: obj;
	const properties = firstItem.properties && typeof firstItem.properties === "object"
		? (firstItem.properties as Record<string, unknown>)
		: {};
	return {
		productId: stringValue(firstItem.product_id ?? firstItem.productId ?? properties.product_id ?? properties.productId),
		productTitle: stringValue(
			firstItem.product_title ??
			firstItem.productTitle ??
			firstItem.title ??
			properties.product_title ??
			properties.productTitle,
		),
		variantId: stringValue(firstItem.variant_id ?? firstItem.variantId ?? firstItem.id ?? properties.variant_id ?? properties.variantId),
		size: stringValue(firstItem.size ?? properties.size),
		recommendedSize: stringValue(firstItem.recommendedSize ?? properties.recommendedSize),
		quantity: numberValue(firstItem.quantity),
	};
}

function extractContextFromBody(body: unknown): CartProductContext {
	if (!body) return {};
	if (body instanceof FormData) {
		return {
			productId: stringValue(body.get("product_id") ?? body.get("productId")),
			productTitle: stringValue(body.get("product_title") ?? body.get("productTitle")),
			variantId: stringValue(body.get("id") ?? body.get("variant_id") ?? body.get("variantId")),
			size: stringValue(body.get("size") ?? body.get("properties[size]")),
			recommendedSize: stringValue(body.get("recommendedSize") ?? body.get("properties[recommendedSize]")),
			quantity: numberValue(body.get("quantity")),
		};
	}
	return extractContextFromObject(tryParseJson(body));
}

async function extractContextFromResponse(response: Response): Promise<CartProductContext> {
	try {
		const contentType = response.headers.get("content-type") || "";
		if (!contentType.includes("application/json")) return {};
		return extractContextFromObject(await response.clone().json());
	} catch {
		return {};
	}
}

function mergeContexts(...contexts: CartProductContext[]): CartProductContext {
	const merged: CartProductContext = {};
	for (const context of contexts) {
		if (merged.productId == null && context.productId != null) merged.productId = context.productId;
		if (merged.productTitle == null && context.productTitle != null) merged.productTitle = context.productTitle;
		if (merged.variantId == null && context.variantId != null) merged.variantId = context.variantId;
		if (merged.size == null && context.size != null) merged.size = context.size;
		if (merged.recommendedSize == null && context.recommendedSize != null) merged.recommendedSize = context.recommendedSize;
		if (merged.quantity == null && context.quantity != null) merged.quantity = context.quantity;
	}
	return merged;
}

function lastWidgetContext(): CartProductContext {
	return {
		productId: lastSizeSelection?.productId ?? lastCompletedProduct?.productId ?? null,
		productTitle: lastSizeSelection?.productTitle ?? lastCompletedProduct?.productTitle ?? null,
		size: lastSizeSelection?.size ?? null,
		recommendedSize: lastSizeSelection?.recommendedSize ?? null,
	};
}

/**
 * Inject `ps_session` into a JSON body that Shopify Ajax cart accepts.
 * Accepts either a `FormData` (from `<form>` submissions) or a plain
 * object body (from Hydrogen / fetch callers).
 */
function injectSessionAttribute(body: unknown): unknown {
	const sessionId = getOrCreateSessionId();
	if (body instanceof FormData) {
		// Shopify accepts the bracketed form syntax.
		body.append(`attributes[${ATTR_KEY}]`, sessionId);
		return body;
	}
	if (typeof body === "string") {
		try {
			const parsed = JSON.parse(body);
			if (parsed && typeof parsed === "object") {
				const withAttr = {
					...(parsed as Record<string, unknown>),
					attributes: {
						...((parsed as { attributes?: Record<string, string> }).attributes ?? {}),
						[ATTR_KEY]: sessionId,
					},
				};
				return JSON.stringify(withAttr);
			}
		} catch {
			return body;
		}
	}
	if (body && typeof body === "object") {
		return {
			...(body as Record<string, unknown>),
			attributes: {
				...((body as { attributes?: Record<string, string> }).attributes ?? {}),
				[ATTR_KEY]: sessionId,
			},
		};
	}
	return body;
}

let installed = false;

/**
 * One-time install. Safe to call multiple times.
 */
export function installCartHook(): void {
	if (typeof window === "undefined" || installed) return;
	installed = true;

	const originalFetch = window.fetch.bind(window);

	window.fetch = async function patchedFetch(input: RequestInfo | URL, init?: RequestInit) {
		const url = typeof input === "string" ? input : input instanceof URL ? input.toString() : input.url;
		if (!isCartAddUrl(url)) {
			return originalFetch(input, init);
		}
		try {
			const patchedInit: RequestInit = { ...(init ?? {}) };
			if (patchedInit.body !== undefined && patchedInit.body !== null) {
				patchedInit.body = injectSessionAttribute(patchedInit.body) as BodyInit;
			}
			const bodyContext = extractContextFromBody(patchedInit.body);
			const response = await originalFetch(input, patchedInit);
			if (response.ok) {
				const responseContext = await extractContextFromResponse(response);
				void postCartAddEvent(mergeContexts(responseContext, bodyContext, lastWidgetContext()));
				// If the widget recorded a size pick for this product, treat this
				// successful cart-add as an implicit SIZE_RECOMMENDATION_ACCEPTED.
				flushSizeAcceptedEvent();
			}
			return response;
		} catch (err) {
			console.warn(TAG, "hook failed, falling back", err);
			return originalFetch(input, init);
		}
	};

	// Some classic themes submit cart forms via XMLHttpRequest. Install a
	// lightweight XHR patch so those also get attributed.
	const OriginalXHR = window.XMLHttpRequest;
	if (OriginalXHR) {
		const origOpen = OriginalXHR.prototype.open;
		const origSend = OriginalXHR.prototype.send;
		OriginalXHR.prototype.open = function open(
			method: string,
			url: string | URL,
			...rest: unknown[]
		) {
			(this as unknown as { __ps_url: string }).__ps_url = typeof url === "string" ? url : url.toString();
			// @ts-expect-error forwarding variadic args
			return origOpen.call(this, method, url, ...rest);
		};
		OriginalXHR.prototype.send = function send(body?: Document | BodyInit | null) {
			const self = this as unknown as { __ps_url?: string };
			if (self.__ps_url && isCartAddUrl(self.__ps_url)) {
				try {
					const patched = body ? injectSessionAttribute(body) : body;
					const bodyContext = extractContextFromBody(patched);
					this.addEventListener(
						"load",
						() => {
							if (this.status < 200 || this.status >= 300) return;
							const responseContext = extractContextFromObject(tryParseJson(this.responseText));
							void postCartAddEvent(mergeContexts(responseContext, bodyContext, lastWidgetContext()));
							flushSizeAcceptedEvent();
						},
						{ once: true },
					);
					return origSend.call(this, patched as XMLHttpRequestBodyInit);
				} catch {
					/* fall through */
				}
			}
			return origSend.call(this, (body ?? null) as XMLHttpRequestBodyInit | null);
		};
	}

	console.log(TAG, "attribution hook installed");
}
